import React, { useEffect, useState } from "react";
import {
    Grid,
    Typography,
    SxProps,
    Theme,
    Button,
    Card,
    CardContent,
    CardActions,
    IconButton,
    CardActionArea,
} from "@mui/material";
import { useParams } from "react-router-dom";
import LearnMoreIcon from "../components/icons/LearnMoreIcon";
import DownloadPDFIcon from "../components/icons/DownloadPDFIcon";
import { CertArticle, CertArticlePreview } from "../adapters/api";
import { useCerts, useIsMobile } from "../hooks";
import PageTitle from "../components/PageTitle";
import PageContent from "../components/PageContent";
import { useTranslation } from "react-i18next";
import backImg from "../assets/back.webp";
import backImgMobile from "../assets/back-mobile.webp";
import { useNavigate } from "react-router-dom";
import AccreditationDetailModal from "../components/AccreditationDetailModal";
import { CommonStyles } from "../styles/CommonStyles";

export const AccreditationCardStyle: Record<string, SxProps<Theme>> = {
    cardContainer: (theme: Theme) => ({
        background: "transparent",
        border: "3px solid",
        borderColor: theme.palette.secondary.main,
        borderRadius: "10px",
        maxHeight: "300px",
        height: "300px",
    }),
    cardContainerMobile: (theme: Theme) => ({
        background: "transparent",
        border: "3px solid",
        borderColor: theme.palette.secondary.main,
        borderRadius: "10px",
        // maxHeight: "250px",
        // height: "auto",
        margin: "20px",
    }),
    cardDate: {
        textAlign: "left",
        color: "#BABABA",
        fontSize: "1rem",
    },
    cardTitle: {
        fontSize: "1.3rem",
        marginTop: "20px",
        textAlign: "left",
        maxHeight: "100px",
        height: "100px",
        width: "100%",
        overflow: "hidden",
        whiteSpace: "collapse",
        textOverflow: "ellipsis",
    },
    cardTitleMobile: {
        fontSize: "20px",
        marginTop: "20px",
        textAlign: "left",
        width: "100%",
        overflow: "hidden",
        whiteSpace: "collapse",
        textOverflow: "ellipsis",
    },
    cardButton: (theme: Theme) => ({
        color: "white",
        marginLeft: "0px !important",
        border: `1px solid transparent`,
        padddingLeft: "10px",
        "&:hover": {
            border: `1px solid ${theme.palette.secondary.main}`,
            background: theme.palette.secondary.dark,
        },
    }),
};

export type AccreditationCardProps = {
    item: CertArticlePreview;
    onLearnMore: () => void;
};

// Card di anteprima delle certificazioni
const AccreditationCard: React.FC<AccreditationCardProps> = ({ item, onLearnMore }) => {
    const [_, setHover] = useState(false);
    const { isMobile } = useIsMobile();
    const { t } = useTranslation();

    return (
        <Card
            sx={
                isMobile
                    ? AccreditationCardStyle.cardContainerMobile
                    : AccreditationCardStyle.cardContainer
            }
        >
            <CardActionArea
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    padding: isMobile ? "10px" : "20px",
                }}
                onClick={onLearnMore}
                onPointerEnter={() => {
                    setHover(true);
                }}
                onPointerLeave={() => {
                    setHover(false);
                }}
            >
                <CardContent sx={{ width: "100%" }}>
                    <Typography sx={AccreditationCardStyle.cardDate}>
                        {item.createdDate}
                    </Typography>
                    <Typography
                        sx={
                            isMobile
                                ? AccreditationCardStyle.cardTitleMobile
                                : AccreditationCardStyle.cardTitle
                        }
                    >
                        {item.title}
                    </Typography>
                </CardContent>
            </CardActionArea>
            <CardActions
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    paddingLeft: isMobile ? "10px" : "20px",
                }}
            >
                <Button
                    sx={AccreditationCardStyle.cardButton}
                    size="small"
                    startIcon={<LearnMoreIcon style={{ marginLeft: "3px" }} />}
                    onClick={onLearnMore}
                >
                    {t("accred.details.learnmore")}
                </Button>
                {
                    item?.attachment &&
                    <Button
                        sx={AccreditationCardStyle.cardButton}
                        size="small"
                        startIcon={<DownloadPDFIcon />}
                        target="_blank"
                        href={"/backend/static/" + item.attachment}
                    >
                        Download PDF
                    </Button>
                }
            </CardActions>
        </Card>
    );
};

export const AccreditationDetailPageStyle: Record<string, SxProps<Theme>> = {
    dialogContainer: (theme: Theme) => ({
        background: "#282334",
        border: "3px solid",
        borderColor: theme.palette.secondary.main,
        borderRadius: "10px",
    }),
    dialogContainerMobile: (theme: Theme) => ({
        background: "#282334",
        border: "1px solid",
        borderColor: theme.palette.secondary.main,
        borderRadius: "10px",
        padding: "8px",
    }),
    dialogTitle: {
        fontSize: "1.5rem",
    },
    dialogTitleMobile: {
        fontSize: "20px",
        h2: {
            marginRight: "10px",
        },
    },
    dialogDate: {
        color: "#BABABA",
    },
    dialogContent: {
        marginTop: "20px",
    },
    dialogContentText: {
        fontSize: "1.2rem",
        color: "white",
    },
    dialogContentTextMobile: {
        fontSize: "16px",
        color: "white",
    },
    dialogButton: {
        fontSize: "1rem",
        color: "white",
    },
    dialogButtonMobile: {
        fontSize: "14px",
        color: "white",
    }
};

type AccreditationDetailPageParams = {
    accreditationType: string;
};

// Pagina di ciascuna categoria specifica di certificazione, contiene le anteprime dei vari articoli
const AccreditationDetailPage = () => {
    const { isMobile } = useIsMobile();
    const navigate = useNavigate();

    const { accreditationType } = useParams<AccreditationDetailPageParams>();
    const {
        getCertsCertification,
        getCertsById,
    } = useCerts();
    const [title, setTitle] = useState("");
    const [certList, setCertList] = useState<CertArticlePreview[]>([]);
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<CertArticle>();

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const fetchData = () => {
        if (accreditationType == null || i18n.resolvedLanguage == null) return;
        getCertsCertification(accreditationType, i18n.resolvedLanguage || "en").then((data) => setCertList(data));
        let title = "";
        switch (accreditationType) {
            case "certifications": {
                title = "accred.cert";
                break;
            }
            case "compliance": {
                title = "accred.compl";
                break;
            }
            case "accreditations": {
                title = "accred.accred";
                break;
            }
            case "policy": {
                title = "accred.policy";
                break
            }
            case "funded": {
                title = "accred.funded";
                break
            }
        }
        setTitle(title);
    };

    useEffect(() => {
        fetchData();

        // eslint-disable-next-line
    }, [i18n.language]);

    return (
        <>
            <AccreditationDetailModal open={open} handleClose={handleClose} selectedItem={selectedItem} key={"AccreditationDetailModal"}/>

            <Grid container>
                <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={isMobile ? CommonStyles.backMobile : CommonStyles.back}
                    onClick={() => navigate("/accreditations")}
                >
                    <img src={isMobile ? backImgMobile : backImg} />
                </IconButton>
                <PageTitle>{title !== "" && t(title)}</PageTitle>
            </Grid>
            
            {/* Griglia anteprime certificazioni */}
            <PageContent>
                <Grid
                    container
                    sx={{ alignItems: "center" }}
                    spacing={isMobile ? 0 : 4}
                >
                    {certList.map((_item) => (
                        <Grid key={_item.id} item xs={12} md={12} lg={4}>
                            <AccreditationCard
                                key={_item.id}
                                item={_item}
                                onLearnMore={() => {
                                    getCertsById(_item.id, i18n.resolvedLanguage || "en").then((data) => {
                                        setSelectedItem(data);
                                        handleClickOpen();
                                    });
                                }}
                            />
                        </Grid>
                    ))}
                </Grid>
            </PageContent>
        </>
    );
};

export default AccreditationDetailPage;
